/**
 * Payment-plan payment helpers (US 4396).
 *
 * A plan is paid through its batches — the BUF/BSS collection batch, or an AR plan's
 * linked open batches — using the same pay-batches checkout as Batches to Pay. The agent
 * types ONE amount; these helpers spread it across the plan's payable batches in the
 * selection/amount shape BatchPaymentModal already understands.
 */
import { getBatchId, roundCurrency, nonNegativeAmount } from '../../powerreporting/utils';

const BUCKETS = [
  { key: 'openBuf', field: 'totalBuf', fallback: 'openBuf' },
  { key: 'openNet', field: 'totalNet', fallback: 'openNet' },
  { key: 'openMisc', field: 'totalMisc', fallback: 'openMisc' },
];

const bucketDue = (batch, bucket) =>
  roundCurrency(nonNegativeAmount(batch?.[bucket.field] ?? batch?.[bucket.fallback]));

/** Everything currently payable on the plan's batches — the most an agent can pay today. */
export const getPlanPayableTotal = (plan) =>
  roundCurrency(
    (plan?.payableBatches || []).reduce(
      (sum, batch) => sum + BUCKETS.reduce((s, bucket) => s + bucketDue(batch, bucket), 0),
      0
    )
  );

/**
 * Default amount for the pay dialog: what brings the plan current (past due + next
 * installment), capped at what is payable. Falls back to the full payable amount when the
 * plan has no schedule.
 */
export const getDefaultPaymentAmount = (plan) => {
  const payable = getPlanPayableTotal(plan);
  const nextDue = roundCurrency(nonNegativeAmount(plan?.nextDueAmount));
  if (!plan?.hasSchedule || nextDue <= 0) return payable;
  return Math.min(nextDue, payable);
};

/**
 * Oldest batch first. Batches-to-pay rows carry no report date (the legacy proc only
 * returns ids and buckets), so the batch id — issued in sequence — is the order; a date
 * is only a tiebreak when one is present.
 */
const byAge = (a, b) =>
  (Number(getBatchId(a)) || 0) - (Number(getBatchId(b)) || 0)
  || new Date(a.reportDate || 0) - new Date(b.reportDate || 0);

/**
 * Spread `amount` over the plan's payable batches, oldest batch first, BUF then NET then
 * MISC within a batch (a collection batch carries its balance as BUF). Never exceeds what
 * is due in any bucket.
 * @returns {{ selectedBatches: Set, paymentAmounts: Object, batches: Array, allocated: number }}
 */
export const allocatePlanPayment = (plan, amount) => {
  const batches = [...(plan?.payableBatches || [])].sort(byAge);
  let remaining = roundCurrency(nonNegativeAmount(amount));
  const selectedBatches = new Set();
  const paymentAmounts = {};

  batches.forEach((batch) => {
    const id = getBatchId(batch);
    let batchTotal = 0;
    BUCKETS.forEach((bucket) => {
      const take = roundCurrency(Math.min(bucketDue(batch, bucket), remaining));
      remaining = roundCurrency(remaining - take);
      batchTotal += take;
      paymentAmounts[`${id}-${bucket.key}`] = take.toFixed(2);
    });
    if (batchTotal > 0) selectedBatches.add(id);
  });

  const allocated = roundCurrency(nonNegativeAmount(amount) - remaining);
  return { selectedBatches, paymentAmounts, batches, allocated };
};
